import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { paymentService } from '../services/paymentService'
import { stripePlans, formatPrice } from '../lib/stripe'
import {
  CheckCircleIcon,
  CameraIcon,
  ArrowRightIcon,
  SparklesIcon,
  CalendarIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline'

const CheckoutSuccessPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshUser } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const [loading, setLoading] = useState(true)
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null)
  
  const sessionId = searchParams.get('session_id')
  const planId = searchParams.get('plan')

  useEffect(() => {
    if (!user || !sessionId || !planId) {
      navigate('/checkout')
      return
    }

    verifyPayment()
  }, [user, sessionId, planId])

  const verifyPayment = async () => {
    if (!user || !sessionId || !planId) return

    try {
      const result = await paymentService.verifyPaymentAndUpdateSubscription(
        sessionId,
        user.id,
        planId
      )

      if (result.success) {
        await refreshUser()
        const plan = stripePlans[planId as keyof typeof stripePlans]
        setSubscriptionDetails({
          plan,
          sessionId,
          activatedAt: new Date(),
          nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        })
        
        showSuccess('Subscription Activated', `Welcome to ${plan.name}! Your subscription is now active.`)
      } else {
        showError('Activation Failed', result.error || 'Failed to activate subscription')
        setTimeout(() => navigate('/checkout'), 3000)
      }
    } catch (error) {
      console.error('Payment verification error:', error)
      showError('Verification Error', 'Failed to verify payment. Please contact support.')
      setTimeout(() => navigate('/checkout'), 3000)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white">Verifying your payment...</p>
        </div>
      </div>
    )
  }

  if (!subscriptionDetails) {
    return null
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-6">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 flex items-center justify-center"
          >
            <CheckCircleIcon className="w-12 h-12 text-white" />
          </motion.div>

          {/* Success Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Welcome to {subscriptionDetails.plan.name}!
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Your subscription has been activated successfully. You now have access to all premium features.
            </p>
          </motion.div>

          {/* Subscription Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-mockup rounded-2xl p-8 mb-8 max-w-2xl mx-auto"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Subscription Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <SparklesIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Plan</h3>
                <p className="text-gray-300">{subscriptionDetails.plan.name}</p>
                <p className="text-blue-400 font-semibold">
                  {formatPrice(subscriptionDetails.plan.price)}/month
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-600 flex items-center justify-center">
                  <CalendarIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Next Billing</h3>
                <p className="text-gray-300">
                  {subscriptionDetails.nextBillingDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center justify-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">
                  Payment Confirmed • Session ID: {subscriptionDetails.sessionId.slice(-8)}
                </span>
              </div>
            </div>
          </motion.div>

          {/* What's Next */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-mockup rounded-2xl p-8 mb-8 max-w-2xl mx-auto"
          >
            <h2 className="text-2xl font-bold text-white mb-6">What's Next?</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4 text-left">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-blue-400 font-semibold text-sm">1</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Explore Premium Features</h3>
                  <p className="text-gray-400 text-sm">
                    Access unlimited projects, AI automation, advanced analytics, and more.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 text-left">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-purple-400 font-semibold text-sm">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Set Up Your Workspace</h3>
                  <p className="text-gray-400 text-sm">
                    Create your first unlimited project and invite team members.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 text-left">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-emerald-400 font-semibold text-sm">3</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Get Support</h3>
                  <p className="text-gray-400 text-sm">
                    Need help? You now have priority support access.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              onClick={() => navigate('/dashboard')}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Go to Dashboard</span>
              <ArrowRightIcon className="w-5 h-5" />
            </motion.button>

            <motion.button
              onClick={() => navigate('/profile')}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all border border-white/20"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <CreditCardIcon className="w-5 h-5" />
              <span>Manage Billing</span>
            </motion.button>
          </motion.div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 text-center"
          >
            <p className="text-gray-400 text-sm">
              A confirmation email has been sent to {user?.email}. 
              You can manage your subscription anytime from your profile settings.
            </p>
          </motion.div>
        </motion.div>
      </main>
    </div>
  )
}

export default CheckoutSuccessPage 